import { BOOLEAN_STATUS, IOperationOcrDetail } from '../../processes/models/processes.models';
import { OPERATION_STATUS } from '../models/operations.models';
import {
	formatFormOperationFieldDisplayValue,
	FormOperationDisplayDeps,
	isFormOperationMatchOcrField,
} from './form-operation-detail-display.util';
import { resolveWorkflowLocaleText } from '../../workflows/utils/workflow-locale-text.util';
import {
	IFormMatchDataError,
	buildFormMatchDataErrorKeySet,
	hasFormOperationMatchDataKo,
	isFormFieldMatchDataMismatch,
	resolveFormMatchDataErrors,
} from './form-operation-match-data.util';
import {
	AuthMatchDataControlName,
	resolveFormFieldAuthMatchDataControl,
} from '../../processes/utils/form-auth-match-data.util';
import {
	IFormFieldMatchOcrResult,
	IFormOperationAnswerField,
	IOperationFormDetail,
	ITextLocaleMap,
} from '../models/operations.models';
import { NOT_AVAILABLE_DISPLAY } from '../../core/utils/utils';

/** Mapeo clave auth match_data → campo plano del snapshot OCR. */
const FORM_MATCH_OCR_REFERENCE_FIELD: Partial<Record<AuthMatchDataControlName, keyof IOperationOcrDetail>> = {
	names: 'names',
	surnames: 'surnames',
	id_document_type: 'document_type',
	identifier: 'identifier',
	birthdate: 'birthdate',
	address: 'address',
	municipality: 'municipality',
	province: 'province',
	country: 'country',
	support_number: 'support_number',
};

export interface IFormMatchCompareTableRow {
	clickValue: string;
	notification: boolean;
	cells: { label: string }[];
}

/** Campos con cotejo `match_ocr` presentes en answers / step_activity. */
export function listFormMatchCompareFields(form: IOperationFormDetail | null | undefined): IFormOperationAnswerField[] {
	if (!form) {
		return [];
	}
	const byKey = new Map<string, IFormOperationAnswerField>();
	const pushUnique = (field: IFormOperationAnswerField) => {
		if (!isFormOperationMatchOcrField(field)) {
			return;
		}
		const key = `${field.id ?? ''}|${field.name ?? ''}|${field.uuid ?? ''}`;
		const existing = byKey.get(key);
		byKey.set(key, existing ? mergeFormMatchCompareField(existing, field) : field);
	};
	for (const step of form.answers ?? []) {
		for (const field of step.fields ?? []) {
			pushUnique(field);
		}
	}
	for (const step of form.step_activity ?? []) {
		const snapshot = step.response_snapshot as { fields?: IFormOperationAnswerField[] } | undefined;
		for (const field of snapshot?.fields ?? []) {
			pushUnique(field);
		}
	}
	for (const field of resolveFormCompareFormFields(form)) {
		pushUnique(field);
	}
	return [...byKey.values()];
}

/** Comparaciones opcionales agregadas en `data.FORM.checks.compare.form_fields` (NIF-553). */
export function resolveFormCompareFormFields(
	form: IOperationFormDetail | null | undefined,
): IFormOperationAnswerField[] {
	const compare = (form?.checks as { compare?: { form_fields?: IFormOperationAnswerField[] } } | undefined)
		?.compare;
	if (!Array.isArray(compare?.form_fields)) {
		return [];
	}
	return compare.form_fields.filter(
		(field) => field && (isFormOperationMatchOcrField(field) || field.match_ocr_result != null),
	);
}

function mergeFormMatchCompareField(
	existing: IFormOperationAnswerField,
	incoming: IFormOperationAnswerField,
): IFormOperationAnswerField {
	return {
		...existing,
		...incoming,
		value: existing.value ?? incoming.value,
		match_ocr_result: incoming.match_ocr_result ?? existing.match_ocr_result,
	};
}

/** Estado de la comprobación «Concordancia con documento de identidad». */
export function resolveFormMatchDataCheckStatus(
	form: IOperationFormDetail | null | undefined,
): BOOLEAN_STATUS | undefined {
	if (!listFormMatchCompareFields(form).length) {
		return undefined;
	}
	return hasFormOperationMatchDataKo(form) ? BOOLEAN_STATUS.KO : BOOLEAN_STATUS.OK;
}

/** El cotejo compara contra datos OCR (operación OCR o snapshot embebido en FORM). */
export function isFormMatchCompareAgainstOcr(
	form: IOperationFormDetail | null | undefined,
	ocrReference?: IOperationOcrDetail | null,
): boolean {
	if (ocrReference) {
		return true;
	}
	if (resolveFormMatchOcrReference(form, null)) {
		return true;
	}
	return Boolean(form?.checks?.ocr_operation_uuid?.trim());
}

export function resolveFormMatchDataLabelKey(
	form: IOperationFormDetail | null | undefined,
	ocrReference?: IOperationOcrDetail | null,
): string {
	return isFormMatchCompareAgainstOcr(form, ocrReference)
		? 'PROCESSES.DETAIL.FORM_MATCH_DATA'
		: 'PROCESSES.DETAIL.FORM_MATCH_DATA_AUTH';
}

export function resolveFormMatchDataModalTitleKey(
	form: IOperationFormDetail | null | undefined,
	ocrReference?: IOperationOcrDetail | null,
): string {
	return isFormMatchCompareAgainstOcr(form, ocrReference)
		? 'PROCESSES.DETAIL.FORM_MATCH_DATA_MODAL.TITLE'
		: 'PROCESSES.DETAIL.FORM_MATCH_DATA_AUTH_MODAL.TITLE';
}

export function resolveFormMatchDataModalAlertKey(
	form: IOperationFormDetail | null | undefined,
	ocrReference?: IOperationOcrDetail | null,
): string {
	return isFormMatchCompareAgainstOcr(form, ocrReference)
		? 'PROCESSES.DETAIL.FORM_MATCH_DATA_MODAL.ALERT'
		: 'PROCESSES.DETAIL.FORM_MATCH_DATA_AUTH_MODAL.ALERT';
}

export function buildFormMatchCompareTableRows(
	form: IOperationFormDetail | null | undefined,
	deps: FormOperationDisplayDeps,
	matchDataErrors?: IFormMatchDataError[] | null,
	ocrReference?: IOperationOcrDetail | null,
): IFormMatchCompareTableRow[] {
	if (!form) {
		return [];
	}
	const ocr = resolveFormMatchOcrReference(form, ocrReference);
	const errors = resolveFormMatchCompareErrors(form, matchDataErrors, ocr);
	const errorKeys = buildFormMatchDataErrorKeySet(errors);
	const comparisonByAuthKey = buildFormMatchOcrResultByAuthKey(form, errors, ocr);

	return listFormMatchCompareFields(form).map((field, index) => {
		const mismatch = resolveFormMatchRowMismatch(field, errorKeys, comparisonByAuthKey);
		const label = resolveFormMatchFieldLabel(field, deps.locale ?? 'es');
		const { formValue, referenceValue } = resolveFormMatchFieldValues(field, deps, comparisonByAuthKey);

		return {
			clickValue: String(index + 1),
			notification: mismatch,
			cells: [
				{ label },
				{ label: formValue },
				{ label: referenceValue },
			],
		};
	});
}

function resolveFormMatchFieldLabel(field: IFormOperationAnswerField, locale: string): string {
	const fromTitle = resolveWorkflowLocaleText(field.title as string | ITextLocaleMap, locale);
	if (fromTitle?.trim()) {
		return fromTitle.trim();
	}
	return (field.name || field.id || NOT_AVAILABLE_DISPLAY).trim();
}

function buildFormMatchOcrResultByAuthKey(
	form: IOperationFormDetail,
	errors: IFormMatchDataError[],
	ocrReference: IOperationOcrDetail | null,
): Map<string, IFormFieldMatchOcrResult> {
	const map = new Map<string, IFormFieldMatchOcrResult>();

	const ingestField = (field: IFormOperationAnswerField): void => {
		if (!isFormOperationMatchOcrField(field) || !field.match_ocr_result) {
			return;
		}
		const authKey = resolveFormFieldAuthMatchDataControl(field);
		if (authKey) {
			map.set(authKey, field.match_ocr_result);
		}
	};

	for (const step of form.answers ?? []) {
		for (const field of step.fields ?? []) {
			ingestField(field);
		}
	}
	for (const step of form.step_activity ?? []) {
		const snapshot = step.response_snapshot as { fields?: IFormOperationAnswerField[] } | undefined;
		for (const field of snapshot?.fields ?? []) {
			ingestField(field);
		}
	}
	for (const field of resolveFormCompareFormFields(form)) {
		ingestField(field);
	}

	for (const legacy of errors) {
		const key = legacy.key?.trim();
		if (!key || map.has(key)) {
			continue;
		}
		map.set(key, {
			status: 'KO',
			form_value: legacy.required_value ?? legacy.response_value ?? null,
			ocr_value: legacy.response_value ?? legacy.required_value ?? null,
		});
	}

	enrichComparisonFromOcrReference(map, form, ocrReference);

	return map;
}

function enrichComparisonFromOcrReference(
	map: Map<string, IFormFieldMatchOcrResult>,
	form: IOperationFormDetail,
	ocrReference: IOperationOcrDetail | null,
): void {
	if (!ocrReference) {
		return;
	}
	for (const field of listFormMatchCompareFields(form)) {
		const authKey = resolveFormFieldAuthMatchDataControl(field);
		if (!authKey || map.has(authKey)) {
			continue;
		}
		const formRaw = String(field.value ?? '').trim();
		const ocrRaw = resolveOcrReferenceRaw(authKey, ocrReference);
		if (!ocrRaw) {
			continue;
		}
		const valuesMatch = areFormMatchCompareValuesEqual(formRaw, ocrRaw);
		map.set(authKey, {
			status: !valuesMatch && isOcrKoMatchDataError(ocrReference) ? 'KO' : 'OK',
			form_value: field.value ?? null,
			ocr_value: ocrRaw,
		});
	}
}

/** Errores match_data desde FORM embebido o snapshot OCR pasado al modal. */
function resolveFormMatchCompareErrors(
	form: IOperationFormDetail,
	explicitErrors: IFormMatchDataError[] | null | undefined,
	ocrReference: IOperationOcrDetail | null,
): IFormMatchDataError[] {
	if (explicitErrors?.length) {
		return explicitErrors;
	}
	const fromForm = resolveFormMatchDataErrors(form);
	if (fromForm.length) {
		return fromForm;
	}
	const fromOcr = (ocrReference as { match_data_errors?: IFormMatchDataError[] } | null)?.match_data_errors;
	if (!Array.isArray(fromOcr)) {
		return [];
	}
	return fromOcr.filter((entry) => Boolean(entry?.key?.trim()));
}

function resolveFormMatchRowMismatch(
	field: IFormOperationAnswerField,
	errorKeys: Set<string>,
	comparisonByAuthKey: Map<string, IFormFieldMatchOcrResult>,
): boolean {
	if (isFormFieldMatchDataMismatch(field, errorKeys)) {
		return true;
	}
	const authKey = resolveFormFieldAuthMatchDataControl(field);
	const comparison = field.match_ocr_result ?? (authKey ? comparisonByAuthKey.get(authKey) : undefined);
	return comparison?.status === 'KO';
}

function normalizeFormMatchCompareValue(value: string): string {
	return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function areFormMatchCompareValuesEqual(formValue: string, referenceValue: string): boolean {
	const formRaw = normalizeFormMatchCompareValue(formValue);
	const refRaw = normalizeFormMatchCompareValue(referenceValue);
	if (!formRaw || !refRaw) {
		return formRaw === refRaw;
	}
	return formRaw === refRaw;
}

function resolveFormMatchOcrReference(
	form: IOperationFormDetail | null | undefined,
	ocrReference?: IOperationOcrDetail | null,
): IOperationOcrDetail | null {
	if (ocrReference) {
		return ocrReference;
	}
	const ocr = form?.checks?.ocr;
	if (!ocr || typeof ocr !== 'object') {
		return null;
	}
	return ocr as unknown as IOperationOcrDetail;
}

function resolveOcrReferenceRaw(authKey: AuthMatchDataControlName, ocr: IOperationOcrDetail): string | undefined {
	const ocrField = FORM_MATCH_OCR_REFERENCE_FIELD[authKey];
	if (!ocrField) {
		return undefined;
	}
	const raw = ocr[ocrField];
	if (raw == null || raw === '') {
		return undefined;
	}
	return String(raw).trim();
}

function resolveFormMatchFieldValues(
	field: IFormOperationAnswerField,
	deps: FormOperationDisplayDeps,
	comparisonByAuthKey: Map<string, IFormFieldMatchOcrResult>,
): { formValue: string; referenceValue: string } {
	const authKey = resolveFormFieldAuthMatchDataControl(field);
	const result = field.match_ocr_result ?? (authKey ? comparisonByAuthKey.get(authKey) : undefined);
	if (result) {
		const formRaw = (result.form_value ?? field.value ?? '').trim();
		const refRaw = (result.ocr_value ?? '').trim();
		return {
			formValue: formRaw
				? formatFormOperationFieldDisplayValue({ ...field, value: formRaw }, deps)
				: NOT_AVAILABLE_DISPLAY,
			referenceValue: refRaw
				? formatFormOperationFieldDisplayValue({ ...field, value: refRaw }, deps)
				: NOT_AVAILABLE_DISPLAY,
		};
	}

	return {
		formValue: formatFormOperationFieldDisplayValue(field, deps),
		referenceValue: NOT_AVAILABLE_DISPLAY,
	};
}

/** KO OCR por cotejo match_data / referencia auth (`error_code` 2014). */
export function isOcrKoMatchDataError(ocr: IOperationOcrDetail | null | undefined): boolean {
	if (!ocr || String(ocr.state ?? '').toUpperCase() !== OPERATION_STATUS.KO) {
		return false;
	}
	return ocr.actions?.KO?.details?.error?.error_code === 2014;
}

/**
 * El cierre KO OCR de cotejo FORM va en Contraste (`FORM_MATCH_DATA`), no en Anti-fraude.
 * Aplica con campos `match_ocr` o con error 2014 en snapshot OCR.
 */
export function shouldSuppressOcrKoInAntifraud(
	ocr: IOperationOcrDetail | null | undefined,
	form: IOperationFormDetail | null | undefined,
): boolean {
	if (isOcrKoMatchDataError(ocr)) {
		return true;
	}
	return listFormMatchCompareFields(form).length > 0;
}
